import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Avatar,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon
} from '@mui/icons-material';
import api from '../services/api';
import { colors as designColors } from '../design-system/tokens';
import NewsletterTabs from '../components/molecules/NewsletterTabs/NewsletterTabs';

const ContactGroups = () => {
  const [groups, setGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manageContactsDialogOpen, setManageContactsDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [group, setGroup] = useState({
    name: '',
    description: '',
    color: designColors.groupColors[0]  // Primeira cor da paleta
  });

  useEffect(() => {
    loadGroups();
    loadContacts();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await api.get('/newsletter/groups');
      setGroups(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await api.get('/newsletter/contacts');
      setContacts(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
    }
  };

  const handleCreateGroup = () => {
    setSelectedGroup(null);
    setGroup({
      name: '',
      description: '',
      color: designColors.groupColors[0]  // Primeira cor da paleta
    });
    setDialogOpen(true);
  };

  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setGroup({
      name: group.name,
      description: group.description,
      color: group.color
    });
    setDialogOpen(true);
  };

  const handleSaveGroup = async () => {
    try {
      setLoading(true);
      if (selectedGroup) {
        await api.put(`/newsletter/groups/${selectedGroup._id}`, group);
      } else {
        await api.post('/newsletter/groups', group);
      }
      setDialogOpen(false);
      loadGroups();
    } catch (error) {
      console.error('Erro ao salvar grupo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este grupo?')) {
      try {
        await api.delete(`/newsletter/groups/${id}`);
        loadGroups();
      } catch (error) {
        console.error('Erro ao excluir grupo:', error);
      }
    }
  };

  const handleManageContacts = (group) => {
    setSelectedGroup(group);
    setManageContactsDialogOpen(true);
  };

  const handleAddContactToGroup = async (contactId) => {
    try {
      await api.post(`/newsletter/groups/${selectedGroup._id}/contacts`, {
        contactId
      });
      loadGroups();
    } catch (error) {
      console.error('Erro ao adicionar contato:', error);
    }
  };

  const handleRemoveContactFromGroup = async (contactId) => {
    try {
      await api.delete(`/newsletter/groups/${selectedGroup._id}/contacts/${contactId}`);
      loadGroups();
    } catch (error) {
      console.error('Erro ao remover contato:', error);
    }
  };

  const getAvailableContacts = () => {
    if (!selectedGroup) return [];
    const groupContactIds = selectedGroup.contacts.map(c => c._id);
    return contacts.filter(contact => !groupContactIds.includes(contact._id));
  };

  const getGroupContacts = () => {
    if (!selectedGroup) return [];
    return selectedGroup.contacts || [];
  };

  // Usando paleta de cores do design system
  const colors = designColors.groupColors;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0, fontWeight: 700, color: 'text.primary' }}>
            <GroupIcon color="primary" />
            Grupos de Contatos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4.5, mb: 3 }}>
            Organize os destinatários das suas newsletters em listas e segmentos.
          </Typography>
        </Box>
      </Box>
      <NewsletterTabs />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateGroup}
        >
          Novo Grupo
        </Button>
      </Box>

      {/* Lista de Grupos */}
      <Grid container spacing={3}>
        {groups.map((group) => (
          <Grid item xs={12} md={6} lg={4} key={group._id}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', transition: 'all 0.2s', '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: 3 } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: group.color,
                      mr: 2,
                      width: 40,
                      height: 40
                    }}
                  >
                    <GroupIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {group.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {group.contacts?.length || 0} contato(s)
                    </Typography>
                  </Box>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {group.description}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {group.contacts?.slice(0, 3).map((contact, index) => (
                    <Chip
                      key={index}
                      label={contact.name}
                      size="small"
                      avatar={<Avatar sx={{ width: 20, height: 20, fontSize: 12 }}>
                        {contact.name.charAt(0)}
                      </Avatar>}
                    />
                  ))}
                  {group.contacts?.length > 3 && (
                    <Chip
                      label={`+${group.contacts.length - 3}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={() => handleManageContacts(group)}
                >
                  Gerenciar
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditGroup(group)}
                >
                  Editar
                </Button>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteGroup(group._id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog de Criação/Edição */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, backgroundImage: 'none' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedGroup ? 'Editar Grupo' : 'Novo Grupo'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nome do Grupo"
              value={group.name}
              onChange={(e) => setGroup({ ...group, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Descrição"
              value={group.description}
              onChange={(e) => setGroup({ ...group, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel>Cor do Grupo</InputLabel>
              <Select
                value={group.color}
                onChange={(e) => setGroup({ ...group, color: e.target.value })}
              >
                {colors.map((color) => (
                  <MenuItem key={color} value={color}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          bgcolor: color,
                          borderRadius: 1,
                          mr: 2
                        }}
                      />
                      {color}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveGroup}
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Gerenciar Contatos */}
      <Dialog open={manageContactsDialogOpen} onClose={() => setManageContactsDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, backgroundImage: 'none' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Gerenciar Contatos - {selectedGroup?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Contatos no Grupo ({getGroupContacts().length})
            </Typography>
            <List>
              {getGroupContacts().map((contact) => (
                <ListItem key={contact._id}>
                  <ListItemText
                    primary={contact.name}
                    secondary={contact.email}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveContactFromGroup(contact._id)}
                      color="error"
                    >
                      <PersonRemoveIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Adicionar Contatos ({getAvailableContacts().length} disponíveis)
            </Typography>
            <List>
              {getAvailableContacts().map((contact) => (
                <ListItem key={contact._id}>
                  <ListItemText
                    primary={contact.name}
                    secondary={contact.email}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleAddContactToGroup(contact._id)}
                      color="primary"
                    >
                      <PersonAddIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageContactsDialogOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContactGroups;
